import { MessagesSquare, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useAssessorControllerListarMinhas,
  useAssessorControllerObterSaldo,
} from '@/api/generated/assessoria/assessoria';
import { AssessorControllerListarMinhasStatus } from '@/api/generated/model';
import { ReforcarSaldoDialogo } from '@/features/assessoria/components/ReforcarSaldoDialogo';
import { CLASSE_STATUS, ROTULO_STATUS } from '@/features/discussoes/rotulos';
import { formatarInstante } from '@/features/exames/rotulos';
import { EstadoErro, EstadoVazio } from '@/shared/components/estados';
import { rotaDaDiscussao } from '@/shared/lib/rotas';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Paginacao } from '@/shared/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const TODOS = 'todos';

/** Espelha `TETO_SALDO_ADVISOR` em `discussoes.constants.ts` — não há endpoint que devolva o teto junto do saldo. */
const TETO_SALDO_ADVISOR = 10;

/**
 * Lado do advisor: discussões já atribuídas a ele (F5) + saldo de vagas
 * semanal. Diferente de `/discussoes` — aquela é "abrir discussão como
 * cliente da plataforma", esta é "atender o que outros médicos abriram".
 */
export function AtendimentosPage() {
  const navigate = useNavigate();
  const [pagina, setPagina] = useState(1);
  const [status, setStatus] = useState<string>(TODOS);
  const [reforcando, setReforcando] = useState(false);

  const { data: saldo, isPending: saldoCarregando } = useAssessorControllerObterSaldo();

  const { data, isPending, isError, refetch } = useAssessorControllerListarMinhas({
    pagina,
    porPagina: 20,
    ...(status !== TODOS ? { status: status as AssessorControllerListarMinhasStatus } : {}),
  });

  function aoFiltrar(aplicar: () => void) {
    aplicar();
    setPagina(1);
  }

  return (
    <div className="space-y-6">
      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold text-brand uppercase tracking-wide mb-1">
            Saldo de vagas desta semana
          </p>
          {saldoCarregando ? (
            <Skeleton className="h-8 w-16" />
          ) : (
            <p className="text-2xl font-bold text-foreground">{saldo?.saldo ?? 0}</p>
          )}
          {saldo && (
            <p className="text-xs text-muted-foreground mt-1">
              Renova toda semana, domingo às 18h — última renovação em {formatarInstante(saldo.renovadoEm)}
            </p>
          )}
        </div>
        <Button
          variant="outline"
          onClick={() => setReforcando(true)}
          disabled={saldoCarregando || (saldo?.saldo ?? 0) > 0}
          title={saldo && saldo.saldo > 0 ? 'Gaste as vagas desta semana antes de reforçar' : undefined}
        >
          <Plus className="w-4 h-4" />
          Reforçar saldo
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <Select value={status} onValueChange={(v) => aoFiltrar(() => setStatus(v))}>
          <SelectTrigger className="sm:w-52" aria-label="Filtrar por status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TODOS}>Todos os status</SelectItem>
            {Object.values(AssessorControllerListarMinhasStatus)
              .filter((valor) => valor !== 'RASCUNHO')
              .map((valor) => (
                <SelectItem key={valor} value={valor}>
                  {ROTULO_STATUS[valor] ?? valor}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-surface-raised rounded-lg border-2 border-brand/20 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-brand/5 to-transparent border-b border-brand/10">
          <h3 className="text-sm font-semibold text-brand uppercase tracking-wide">
            Discussões atribuídas a você
          </h3>
        </div>

        {isPending ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 rounded-md" />
            ))}
          </div>
        ) : isError || !data ? (
          <EstadoErro aoTentarDeNovo={() => void refetch()} />
        ) : data.itens.length === 0 ? (
          <EstadoVazio
            icone={MessagesSquare}
            titulo="Nenhum atendimento ainda"
            descricao={
              status !== TODOS
                ? 'Tente outro status.'
                : 'As discussões que médicos abrirem com você aparecem aqui, assim que o pagamento for confirmado.'
            }
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Assunto</TableHead>
                    <TableHead>Médico</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Não lidas</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.itens.map((discussao) => (
                    <TableRow
                      key={discussao.id}
                      className="cursor-pointer"
                      onClick={() => navigate(rotaDaDiscussao(discussao.id))}
                    >
                      <TableCell className="font-mono text-xs text-muted-foreground">
                        {discussao.codigo}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-foreground">{discussao.assunto}</span>
                        {discussao.pacienteNome && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {discussao.pacienteNome}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="text-foreground">{discussao.autorNome}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={CLASSE_STATUS[discussao.status]}>
                          {ROTULO_STATUS[discussao.status] ?? discussao.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {discussao.naoLidas > 0 ? (
                          <Badge className="bg-brand text-brand-foreground">
                            {discussao.naoLidas}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <Paginacao
              pagina={data.pagina}
              totalPaginas={data.totalPaginas}
              total={data.total}
              porPagina={data.porPagina}
              aoMudarPagina={setPagina}
            />
          </>
        )}
      </div>

      {saldo && (
        <ReforcarSaldoDialogo
          aberto={reforcando}
          aoFechar={() => setReforcando(false)}
          saldoAtual={saldo.saldo}
          teto={TETO_SALDO_ADVISOR}
        />
      )}
    </div>
  );
}
